import { withQuery, type QueryParams } from './query'
import { createFetchTransport, type FetchTransportConfig, type HttpMethod, type Transport } from './transport'
import { createStoriesResource, type StoriesResource } from './resources/stories'

/** Per-call options every SDK method accepts. */
export interface RequestOptions {
  signal?: AbortSignal
  /** Passed untouched to the transport (see TransportRequest.options). */
  transport?: Record<string, unknown>
}

/** The typed request primitive the resources are built on; also exposed for one-off calls. */
export type RequestFn = <T>(method: HttpMethod, path: string, query?: QueryParams, options?: RequestOptions, body?: unknown) => Promise<T>

export interface NewsdeskClient {
  request: RequestFn
  stories: StoriesResource
}

export type ClientConfig =
  | { transport: Transport }
  | FetchTransportConfig

/**
 * Build a client. Pass `transport` to route requests through the host's own
 * HTTP stack (an axios instance with its auth and caching, for instance), or
 * pass a `baseUrl` and the SDK uses fetch.
 */
export function createNewsdeskClient(config: ClientConfig): NewsdeskClient {
  const transport: Transport = 'transport' in config ? config.transport : createFetchTransport(config)

  const request: RequestFn = <T>(method: HttpMethod, path: string, query?: QueryParams, options?: RequestOptions, body?: unknown) =>
    transport({ method, url: withQuery(path, query), body, signal: options?.signal, options: options?.transport }) as Promise<T>

  return {
    request,
    stories: createStoriesResource(request)
  }
}
